'use client'

import { Fragment, useEffect, useState, type ReactNode } from 'react'
import ReviewHero from './ReviewHero'
import ReviewSidebar from './ReviewSidebar'
import Container from '@/components/structure/Container'
import Section from '@/components/content/common/Section'
import RichText from '@/components/content/common/RichText'
import Tree from '@/components/content/sidebar/Tree'
import Timestamp from '@/components/ui/Timestamp'
import Authors from '@/components/content/common/Authors'
import VideoPlayer from '@/components/content/videos/VideoPlayer'
import ScreenshotCarousel, {
  buildScreenshotEntries,
} from '@/components/content/common/ScreenshotCarousel'
import Link from '@/components/ui/Link'
import Pill from '@/components/ui/Pill'
import DealClaimButton from '@/components/content/deals/DealClaimButton'
import Highlight from '@/components/ui/Highlight'
import CategoryOverview from '@/components/ui/CategoryOverview'
import DealsTrending from '@/components/content/deals/root/DealsTrending'
import ComparisonRowThumbnail from '@/components/content/comparisons/root/ComparisonsRow'
import { DealOverview, DealEligibility } from '@/components/content/deals/deal/DealSummary'
import TabToggle from '@/components/navigation/Toggles'
import Meta from '@/components/structure/Meta'
import RelatedComparisons from '@/components/content/common/RelatedComparisons'
import AppRanking from '@/components/content/app/AppRanking'
import PreferredSource from '@/components/ui/PreferredSource'
import { Icon } from '@/icons/Icon'
import { FeaturedPlacement, trackFeaturedClick } from '@/lib/analytics'
import { getReviewAwardCategory, isPopulatedObject, toSlug } from '@/lib/utils'
import { createLogger } from '@/lib/logger'
import { generateStructure, renderContent } from '@/lib/content'
import { PageContextProvider, usePageContext } from '@/components/content/common/PageContext'
import { extractBlogSlugUrlFromFaq } from '@/lib/formatting'
import {
  Recommendation,
  Classification,
} from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import { generateMenu } from '@/helpers/menus'
import { pickWeightedPartner, isGoldOrPlatinumPartner } from '@/components/content/app/helpers'
import { fetchTrendingPartnerCandidates } from '@/client-fetch-helpers'
import { PartnershipTier } from '@/collections/App/constants'
import ExploreButton from '@/components/content/common/ExploreButton'
import type { Block, SectionItem } from '@/types/app'
import type { BylineAuthor } from '@/lib/fetch-authors'
import type { RankChange } from '@/components/ui/RankIndicator'
import type { ReviewComparisonTarget } from '@/lib/comparison/getReviewComparisonTarget'
import type {
  App,
  AppCategoryRecommendation,
  Category,
  Review,
  ReviewCategoryCriterionRating,
  Video,
  ReviewsFaq,
  Faq,
  Deal,
  Comparison,
  VideoAppsApp,
} from '@/payload-types'

const logger = createLogger('ReviewContainer')

type ReviewContainerProps = {
  app: Pick<
    App,
    | 'id'
    | 'icon'
    | 'slug'
    | 'glyph'
    | 'categoryRecommendations'
    | 'brandBackgroundColor'
    | 'brandPrimaryColor'
    | 'brandSecondaryColor'
    | 'brandTertiaryColor'
    | 'websiteUrl'
    | 'name'
    | 'screenshots'
    | 'homepageImage'
    | 'partnershipTier'
    | 'affiliateLink'
  >
  review: Pick<
    Review,
    | 'id'
    | 'tldr'
    | 'summary'
    | 'bestFor'
    | 'alternativeSummary'
    | 'attribute'
    | 'name'
    | 'review'
    | 'seoTitle'
    | 'faqs'
    | 'createdAt'
    | 'reviewCategoryCriterionRating'
  >
  reviewName: string
  videoApp?: VideoAppsApp
  comparisons: Pick<Comparison, 'id' | 'name' | 'comparisonApps' | 'tldr'>[]
  deals: Deal[]
  categories: Array<
    Category & {
      recommendation: Recommendation
      classification: Classification
    }
  >
  alternativeApps?: AppCategoryRecommendation[]
  hasAlternatives: boolean
  modifiedTimestamp: string
  authors?: BylineAuthor[]
  rank?: number
  rankChange?: RankChange
  comparisonTarget?: ReviewComparisonTarget | null
}

export default function ReviewContainer(props: ReviewContainerProps) {
  return (
    <PageContextProvider initialTab="Review">
      <ReviewContainerContent {...props} />
    </PageContextProvider>
  )
}

export function ReviewContainerContent({
  app,
  review,
  reviewName,
  videoApp,
  comparisons,
  deals,
  categories,
  alternativeApps,
  hasAlternatives,
  modifiedTimestamp,
  authors,
  rank,
  rankChange,
  comparisonTarget,
}: ReviewContainerProps) {
  // Comparison Pagination
  const { pageTab, setPageTab, switchFocus, playVideo, setPlayVideo } = usePageContext()
  const publishedTimestamp = review.createdAt

  // "Trending Right Now" is the non-partner fallback shown when there are no category
  // alternatives. The candidate fetch + weighted pick run on the client so the pick
  // re-rolls on refresh; the gate is known at SSR, so the skeleton reserves height (no CLS).
  // TODO: defer this fetch behind useDeferredActivation (after-interaction-idle) so it stays
  // out of the initial-load / Lighthouse window, converging with the hero animation trigger.
  const isNonPartnerApp =
    !app.partnershipTier ||
    [PartnershipTier.Bronze, PartnershipTier.Sponsored].includes(
      app.partnershipTier as PartnershipTier,
    )
  const showTrendingSlot = isNonPartnerApp && (alternativeApps?.length ?? 0) === 0

  const [trendingApp, setTrendingApp] = useState<AppCategoryRecommendation | undefined>(undefined)
  const [trendingLoaded, setTrendingLoaded] = useState(false)

  useEffect(() => {
    if (!showTrendingSlot) return
    let cancelled = false
    // Clear any previous partner so a stale pick can't flash while the new fetch
    // runs (the effect re-runs when app.id changes on client navigation).
    setTrendingApp(undefined)
    setTrendingLoaded(false)
    async function loadTrending() {
      try {
        const candidates = await fetchTrendingPartnerCandidates()
        const eligible = candidates.filter((candidate) => candidate.id !== app.id)
        const picked = pickWeightedPartner(eligible)
        if (cancelled) return
        setTrendingApp(picked ? ({ app: picked } as AppCategoryRecommendation) : undefined)
      } catch (error) {
        if (!cancelled) {
          logger.error('Failed to load trending partner candidates', error)
          setTrendingApp(undefined)
        }
      } finally {
        // Always clear the loading state (unless unmounted) so the trending
        // skeleton can't get stuck spinning when the fetch rejects.
        if (!cancelled) setTrendingLoaded(true)
      }
    }
    loadTrending()
    return () => {
      cancelled = true
    }
  }, [showTrendingSlot, app.id])

  const hasVideo = isPopulatedObject<Video>(videoApp?.video)
  if (videoApp && !hasVideo) {
    logger.warn('videoApp.video is not populated in ReviewContainer')
  }

  const { roots, preamble } = generateStructure({
    content: review?.review?.root?.children as Block[],
  })
  const menu = generateMenu('app', {
    app,
    deals,
    setPlayVideo: hasVideo ? setPlayVideo : undefined,
    resetTab: () => setPageTab('Review'),
    isReview: true,
  })

  const awardCategory = getReviewAwardCategory(app)
  const visibleCategories = categories.filter(
    (category) =>
      !(
        category.classification === Classification.Secondary &&
        category.recommendation === Recommendation.NotRecommended
      ),
  )

  const efficiencyPoints = (() => {
    if (!awardCategory) return undefined
    const ratings = (review.reviewCategoryCriterionRating?.docs ?? []).filter(
      (d): d is ReviewCategoryCriterionRating => typeof d !== 'number',
    )
    const categoryRatings = ratings.filter((r) => {
      const cc = typeof r.categoryCriterion === 'number' ? null : r.categoryCriterion
      const cat = cc && typeof cc.category !== 'number' ? cc.category : null
      const catId = cat?.id ?? (typeof cc?.category === 'number' ? cc.category : null)
      return catId === awardCategory.id && r.isApplicable !== false
    })
    if (categoryRatings.length === 0) return undefined
    return categoryRatings.reduce((acc, r) => acc + (r.rating && r.rating >= 5 ? 1 : 0), 0)
  })()

  // Review Summary
  const tldrSection: SectionItem[] = [
    {
      title: `Review Summary`,
      content: (
        <div className="flex flex-col space-y-4">
          <RichText id={app.slug} content={(review?.tldr?.root.children as Block[]) || []} />
          {hasVideo && videoApp && (
            <VideoPlayer
              video={videoApp.video as Video}
              videoApps={[videoApp]}
              autoPlay={playVideo}
              timestamp={videoApp.timestampStart}
              timestampEnd={videoApp.timestampEnd}
              isExpanded={true}
            />
          )}
        </div>
      ),
    },
  ]

  // Review
  const { sections: reviewSection, preamble: reviewPreamble } = renderContent({
    roots,
    preamble,
  })

  const screenshotEntries = buildScreenshotEntries([app])
  const screenshotSection: SectionItem[] =
    screenshotEntries.length > 0
      ? [
          {
            title: `Screenshots`,
            content: <ScreenshotCarousel entries={screenshotEntries} />,
          },
        ]
      : []

  // Alternatives
  const alternativesSection =
    alternativeApps && alternativeApps.length > 0
      ? [
          {
            title: `${app.name} Alternatives`,
            content: (
              <>
                <p>
                  Not sure if {app.name} is the right fit for you? Check out these alternatives:
                </p>

                <AppRanking
                  apps={alternativeApps}
                  category={awardCategory as Category}
                  featuredPlacement={FeaturedPlacement.ALTERNATIVES}
                />
              </>
            ),
          },
        ]
      : []

  // Categories
  const categoriesSection: SectionItem[] = [
    {
      title: `Categories`,
      content: (
        <div className="flex flex-col gap-4">
          <p>
            <span className="font-medium text-gray-950">{app.name}</span> fits into multiple
            categories based on what it actually helps you do. Each category highlights a different
            strength and the{' '}
            <Link href="/criteria" className="underlined primary">
              efficiency points
            </Link>{' '}
            it earned, helping you compare tools not just by features, but by how well they actually
            perform.
          </p>
          {awardCategory ? (
            <CategoryOverview
              reviewId={review.id}
              app={app}
              categories={visibleCategories}
              reviewCriterionRatings={review.reviewCategoryCriterionRating?.docs?.filter(
                (d): d is ReviewCategoryCriterionRating => typeof d !== 'number',
              )}
            />
          ) : null}

          <p>
            Keep exploring the{' '}
            <Link href="/best" className="underlined primary">
              best software
            </Link>{' '}
            across categories
            {hasAlternatives && (
              <>
                , or explore{' '}
                <Link href={`/alternatives/${app.slug}`} className="underlined primary">
                  {app.name} alternatives
                </Link>
              </>
            )}
          </p>
        </div>
      ),
    },
  ]

  // FAQ
  const faqSection: SectionItem[] =
    review?.faqs?.docs && review.faqs.docs.length > 0
      ? [
          {
            title: 'FAQ',
            content: (
              <div className="flex flex-col gap-4">
                {review.faqs.docs.map((section, sectionIndex) => {
                  const faq = (section as ReviewsFaq).faq as Faq

                  return (
                    <Section
                      key={`faq-${sectionIndex}`}
                      sectionId={`faq-${toSlug(faq.question ?? 'Untitled')}`}
                      title={faq.question ?? 'Untitled'}
                      isChild={true}
                      icon={<Icon name="circle-question" />}
                      collapsed={sectionIndex > 0}
                      renderCollapsedContent={true}
                    >
                      {faq.answer?.root?.children ? (
                        <RichText
                          content={faq.answer.root.children as Block[]}
                          readMore={extractBlogSlugUrlFromFaq(faq)}
                        />
                      ) : (
                        <p>No answer available</p>
                      )}
                    </Section>
                  )
                })}
              </div>
            ),
          },
        ]
      : []

  // Deals
  const dealsSection: SectionItem[] =
    deals.map((doc) => {
      const deal = doc as Deal

      return {
        title: deal.name ?? 'Untitled',
        content: (
          <div className="flex flex-col gap-4">
            <p>{deal.terms}</p>
            <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 xs:flex-row xs:items-center">
              <div className="flex flex-col lg:flex-row lg:items-center lg:gap-3">
                <DealOverview deal={deal} invert={true} />
                <DealEligibility
                  eligibility={deal.eligibility}
                  terms={deal.terms}
                  invert={true}
                  hideInfo={true}
                />
              </div>
              <DealClaimButton
                deal={deal}
                app={app}
                onClick={() => trackFeaturedClick(FeaturedPlacement.REVIEW_HERO, app.name)}
                className="xs:ml-auto"
              >
                <Pill style="outlinePrimary">Claim</Pill>
              </DealClaimButton>
            </div>
          </div>
        ),
      }
    }) ?? []

  // Comparisons
  const comparisonsSection: SectionItem[] =
    comparisons.length > 0
      ? comparisons.map((comparison) => {
          return {
            title: comparison.name ?? 'Untitled',
            content: (
              <div className="flex flex-col gap-4">
                <RichText content={comparison.tldr?.root?.children as Block[]} />
                <ComparisonRowThumbnail comparison={comparison} inline={true} />
              </div>
            ),
          }
        })
      : []

  // Trending App: skeleton until the client pick resolves (reserves height -> no CLS),
  // the app once picked, nothing if the pick came back empty (rare).
  const trendingAppSection =
    showTrendingSlot && (!trendingLoaded || (trendingApp && trendingApp.app)) ? (
      <Fragment key={`trending`}>
        <Section title="Trending Right Now" ignoreHeading={true}>
          {trendingApp && trendingApp.app ? (
            <AppRanking
              apps={[trendingApp]}
              category={awardCategory as Category}
              featuredPlacement={FeaturedPlacement.TRENDING}
            />
          ) : (
            <div
              aria-hidden="true"
              className="min-h-[72px] w-full animate-pulse rounded-xl border border-white/15 bg-white/5"
            />
          )}
        </Section>
      </Fragment>
    ) : null

  // Structure
  const content: SectionItem[] = (() => {
    switch (pageTab) {
      case 'Review':
        return [
          ...tldrSection,
          ...alternativesSection,
          ...reviewSection,
          ...screenshotSection,
          ...categoriesSection,
          ...faqSection,
        ]
      case 'Deals':
        return [...dealsSection]
      case 'Comparison':
        return [...comparisonsSection]
      default:
        return []
    }
  })()

  return (
    <>
      <Meta
        modifiedTimestamp={modifiedTimestamp}
        publishedTimestamp={publishedTimestamp}
        selectedLabel={reviewName}
        parent={
          awardCategory
            ? { label: awardCategory.name, url: `/best/${awardCategory.slug}` }
            : undefined
        }
      />
      <ReviewHero
        app={app}
        reviewName={reviewName}
        category={awardCategory}
        reviewId={review.id}
        reviewCriterionRatings={review.reviewCategoryCriterionRating?.docs}
        efficiencyPoints={efficiencyPoints}
        rank={rank}
        rankChange={rankChange}
        timestamps={{
          modified: modifiedTimestamp,
          published: publishedTimestamp,
        }}
      >
        {deals.length > 0 && (
          <Highlight
            onClick={() => switchFocus('Deals')}
            icon={<Icon name="ticket" />}
            primary={`${deals.length} deal${deals.length === 1 ? '' : 's'}`}
            secondary={`${deals.reduce((sum, deal) => sum + (deal.claimed ?? 0), 0)} claims`}
            // Live aggregate — drifts between Meticulous runs as users claim deals.
            secondaryClassName="meticulous-ignore"
          />
        )}
        {comparisons.length > 0 && (
          <Highlight
            onClick={() => switchFocus('Comparison')}
            icon={<Icon name="comparison" />}
            primary={`${comparisons.length} comparison${comparisons.length === 1 ? '' : 's'}`}
            secondary={`${comparisons.length} alternatives`}
          />
        )}
      </ReviewHero>

      <article id="content" className="overlay">
        <Container
          sidebar={
            <>
              <Tree
                roots={content}
                header={
                  <ReviewSidebar
                    app={app}
                    compareWithApp={comparisonTarget?.app}
                    canonicalCompareSlug={comparisonTarget?.canonicalSlug}
                    reviewName={reviewName}
                    category={awardCategory}
                    menu={menu}
                    rank={rank}
                    rankChange={rankChange}
                  />
                }
              />
              <PreferredSource invert={true} />
              <div className="hidden flex-wrap items-center gap-y-2.5 md:flex">
                {(modifiedTimestamp || publishedTimestamp) && (
                  <Timestamp
                    modified={modifiedTimestamp}
                    published={publishedTimestamp}
                    lead="Last updated on"
                    trail="by"
                    className="pr-1"
                  />
                )}
                <Authors authors={authors} condensed={true} eagerImage={true} />
              </div>
              {/* Hide the generic deals cross-sell when this app is itself a
                  Gold/Platinum partner — its own deal/CTA already leads. */}
              {!isGoldOrPlatinumPartner(app.partnershipTier) && <DealsTrending />}
            </>
          }
          mobilePadding={false}
        >
          <TabToggle
            defaultValue="Review"
            defaultIcon={<Icon name="review" />}
            deals={deals.length}
            comparisonCount={comparisons.length}
          />

          {content.map((section, index) => {
            const isFirstReviewSection =
              pageTab === 'Review' && index === tldrSection.length && reviewPreamble
            return (
              <Fragment key={`${section.title}-${index}`}>
                {isFirstReviewSection && <div className="px-5 pb-2">{reviewPreamble}</div>}
                <Section
                  title={section.title || ''}
                  isParent={section.title === 'FAQ' || (section.children?.length ?? 0) > 0}
                >
                  {section.content as ReactNode}
                </Section>
                {section.title === 'Review Summary' &&
                  alternativesSection.length === 0 &&
                  trendingAppSection}
              </Fragment>
            )
          })}
        </Container>

        <Container reduced={true}>
          {awardCategory && (
            <RelatedComparisons comparisons={comparisons} categoryId={awardCategory.id} />
          )}
          <ExploreButton label="Explore app reviews" link="/apps" />
        </Container>
      </article>
    </>
  )
}
